/**
 * Dependency imports
 */
import React from 'react';
import Image from 'next/image';
import styled from '@emotion/styled';
import { css } from '@emotion/react';
import { Box, BoxProps } from '@mantine/core';

/**
 * Local imports
 */

/**
 * Root
 */
const Root = styled(Box, {
  shouldForwardProp: (prop: PropertyKey) => ![''].includes(prop.toString()),
})<Partial<AppIsometricCombinedProps>>(
  (props) => css`
    pointer-events: none;
    height: 65vh;
    overflow: hidden;

    .windows {
      position: relative;
      display: flex;
      align-items: center;
      justify-content: center;
    }

    .main-window {
      position: relative;
      transform: rotateZ(-30deg) rotateY(40deg) rotateX(40deg);
    }

    .window-1 {
      position: absolute;
      top: -5%;
      left: 55%;
      z-index: 2;
      transform: rotateZ(-30deg) rotateY(40deg) rotateX(40deg);
      box-shadow: -50px 54px 50px 0 #00000050;
    }

    .window-2 {
      position: absolute;
      top: 21%;
      left: 36%;
      z-index: 1;
      transform: rotateZ(-30deg) rotateY(40deg) rotateX(40deg);
      box-shadow: -40px 50px 40px 0 #00000060;
    }

    .window-3 {
      position: absolute;
      top: 23%;
      left: 26%;
      transform: rotateZ(-30deg) rotateY(40deg) rotateX(40deg);
      box-shadow: -10px 10px 10px 0 #00000030;
    }

    .window-4 {
      position: absolute;
      top: 27%;
      left: 51%;
      z-index: 3;
      transform: rotateZ(-30deg) rotateY(40deg) rotateX(40deg);
      box-shadow: -110px 110px 40px 0 #00000030;
    }
  `
);

/**
 * AppIsometric Component
 */
export interface AppIsometricProps {
  [prop: string]: any;
}

export type AppIsometricCombinedProps = AppIsometricProps & BoxProps;

const AppIsometric: React.FC<AppIsometricCombinedProps> = (props) => {
  return (
    <Root {...props}>
      <div className="windows">
        <picture>
          <source srcSet="/img/assets/window.webp" type="image/webp" />
            <img
              className="main-window"
              src="/img/assets/window.jpg"
              alt="Home Health Solutions evaluating a Balmoral Condo Document Review, 
          displaying statistics on units, parking, document types, 
          and an overview of the condo documents within the client dashboard."
              width={1000}
            height={1000}
            style={{ borderRadius: 20 }}
          />
        </picture>

        <picture>
  <source srcSet="/img/assets/window-1.webp" type="image/webp" />
  <img
    className="window-1"
    src="/img/assets/window-1.jpg"
    alt="Interactive Health Doc Chat by Home Health Solutions, featuring AI-driven tools that enable user engagement in reviewing their condo documents efficiently and effectively."
    width={1184 / 3}
    height={1965 / 3}
    style={{ borderRadius: 20 }}
  />
</picture>

        <picture>
  <source srcSet="/img/assets/window-2.webp" type="image/webp" />
  <img
    className="window-2"
    src="/img/assets/window-2.jpg"
    alt="Key documents section in the Balmoral Condo Document Review, showcasing essential information such as bylaws, financial statements, meeting minutes, and other critical reports to ensure a thorough evaluation of the condominium's health."
    width={745 / 3}
    height={1965 / 3}
    style={{ borderRadius: 20 }}
  />
</picture>

        <picture>
  <source srcSet="/img/assets/window-3.webp" type="image/webp" />
  <img
    className="window-3"
    src="/img/assets/window-3.jpg"
    alt="Overview of tasks generated from Condo Document Reviews over time, highlighting ongoing and completed actions to ensure comprehensive condo health assessments by Home Health Solutions."
    width={1078 / 3}
    height={822 / 3}
    style={{ borderRadius: 20 }}
  />
</picture>

        <picture>
  <source srcSet="/img/assets/window-4.webp" type="image/webp" />
  <img
    className="window-4"
    src="/img/assets/window-4.jpg"
    alt="Tasks summary highlighting outstanding actions from Condo Document Reviews, ensuring all critical items are addressed for a thorough evaluation by Home Health Solutions."
    width={1078 / 3}
    height={822 / 3}
    style={{ borderRadius: 20 }}
  />
</picture>
      </div>
    </Root>
  );
};

export default AppIsometric;
